// AYA Multiservice — Écran 5 : résultat et reçu (simulation).
import React from "react";
import { Check, X, Clock, Share2, History, RotateCcw, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { BrandLogo } from "./BrandLogo";
import { fmtFcfa } from "./multiserviceApi";

const FAIL_TEXT = {
  payment_failed: "Le paiement simulé n'a pas pu aboutir. Aucune somme n'a été débitée.",
  recharge_failed: "Le paiement simulé a été accepté, mais la recharge simulée n'a pas pu être transmise. Aucun mouvement réel n'a eu lieu.",
};

export default function ResultStep({ catalog, order, onNewRecharge, onRetry, onBackToConfig, onHistory, retrying }) {
  if (!order) return null;
  const op = (catalog?.operators || []).find((o) => o.id === order.operator_id);
  const dateFr = order.updated_at ? new Date(order.updated_at).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
  const statusFr = { success: "Réussie (simulation)", failed: "Échec (simulation)", pending: "En attente (simulation)", unknown: "Indéterminé (simulation)" }[order.status] || order.status;

  const receiptText = () =>
    `REÇU DE SIMULATION AYA (aucun paiement réel)\nOpérateur : ${order.operator_name}\nNuméro : ${order.beneficiary_masked}\nOffre : ${order.offer_name}\nMontant simulé : ${fmtFcfa(order.total_amount)}\nPaiement : ${order.payment_method_name}\nDate : ${dateFr}\nRéférence : ${order.mock_reference}\nStatut : ${statusFr}`;

  const share = async () => {
    const text = receiptText();
    if (navigator.share) {
      try { await navigator.share({ title: "Reçu de simulation AYA", text }); return; } catch { /* annulé */ }
    }
    try { await navigator.clipboard.writeText(text); toast.success("Reçu de simulation copié."); }
    catch { toast.error("Impossible de copier le reçu."); }
  };

  const Details = () => (
    <div className="ms-card" style={{ textAlign: "left" }} data-testid="ms-receipt-card">
      <div className="ms-card-head">
        {op && <BrandLogo brand={op.brand} size={36} />}
        <b>{order.operator_name}</b>
        <span className={`ms-status ${order.status}`}>{statusFr.toUpperCase()}</span>
      </div>
      <div className="ms-line"><span>Numéro</span><b data-testid="ms-result-phone">{order.beneficiary_masked}</b></div>
      <div className="ms-line"><span>Offre</span><b>{order.offer_name}</b></div>
      <div className="ms-line"><span>Montant simulé</span><b data-testid="ms-result-amount">{fmtFcfa(order.total_amount)}</b></div>
      <div className="ms-line"><span>Moyen de paiement</span><b>{order.payment_method_name}</b></div>
      <div className="ms-line"><span>Date et heure</span><b>{dateFr}</b></div>
      <div className="ms-line"><span>Référence</span><b data-testid="ms-result-reference">{order.mock_reference}</b></div>
    </div>
  );

  if (order.status === "success") {
    return (
      <div className="ms-result" data-testid="ms-result-success">
        <span className="ms-result-badge ok"><Check size={40} color="#fff" strokeWidth={3} /></span>
        <h2>Recharge simulée réussie</h2>
        <p>Le traitement de démonstration est terminé.</p>
        <Details />
        <div className="ms-demo-note" style={{ textAlign: "left" }} data-testid="ms-result-demo-note">
          Opération de démonstration : aucun mouvement financier réel n'a eu lieu.
        </div>
        <button className="ms-cta green" data-testid="ms-share-receipt-btn" onClick={share}><Share2 size={16} /> Partager le reçu (simulation)</button>
        <button className="ms-cta" data-testid="ms-new-recharge-btn" onClick={onNewRecharge}>Nouvelle recharge</button>
        <button className="ms-btn-outline" data-testid="ms-goto-history-btn" onClick={onHistory}><History size={15} /> Consulter l'historique</button>
      </div>
    );
  }

  if (order.status === "failed") {
    return (
      <div className="ms-result" data-testid="ms-result-failed">
        <span className="ms-result-badge ko"><X size={40} color="#fff" strokeWidth={3} /></span>
        <h2>La simulation a échoué</h2>
        <p>{FAIL_TEXT[order.failure_reason] || "Le traitement simulé n'a pas pu aboutir."}</p>
        <Details />
        <button className="ms-cta" data-testid="ms-retry-btn" disabled={retrying} onClick={onRetry}><RotateCcw size={16} /> {retrying ? "Nouvelle tentative…" : "Réessayer"}</button>
        <button className="ms-btn-outline" data-testid="ms-back-config-btn" onClick={onBackToConfig}><Settings2 size={15} /> Revenir à la configuration</button>
        <button className="ms-btn-outline" data-testid="ms-goto-history-btn" onClick={onHistory}><History size={15} /> Détail dans l'historique</button>
      </div>
    );
  }

  return (
    <div className="ms-result" data-testid="ms-result-pending">
      <span className="ms-result-badge wait"><Clock size={38} color="#fff" strokeWidth={2.6} /></span>
      <h2>{order.status === "unknown" ? "Résultat indéterminé" : "Résultat en attente"}</h2>
      <p>La simulation n'a pas encore de résultat confirmé. La recharge n'est pas considérée comme réussie.</p>
      <Details />
      <button className="ms-btn-outline" data-testid="ms-goto-history-btn" onClick={onHistory}><History size={15} /> Suivre dans l'historique</button>
      <button className="ms-cta" data-testid="ms-new-recharge-btn" onClick={onNewRecharge}>Nouvelle recharge</button>
    </div>
  );
}
